import { StoredTemplate } from '../types/Individualization'
import jsPDF from 'jspdf'
import { svg2pdf } from 'svg2pdf.js'
import { Font } from '../types/Font'
import { fabric } from 'fabric'
import { rescaleFabricObject } from './resizeEverything'
import { loadClipPath } from './loadClipPath'
import { loadInputs } from './fulfillment'
import { Customization } from '../types/Customization'

const convertToTemplateString = (template: StoredTemplate, fonts: Font[], customization: Customization) => {
    if (!template.canvas) {
        return
    }

    // use clone of the canvas to avoid modifying the original
    const canvas: fabric.Canvas = fabric.util.object.clone(template.canvas)

    if (
        canvas
            .getObjects()
            .filter((obj) => obj.data && obj.data.type === 'user_input')
            .length === 0
    ) {
        return
    }

    canvas._objects = canvas._objects.map((obj) => {
        if (obj.type !== 'image') return rescaleFabricObject(obj)
        return obj
    })

    // remove background image
    canvas.backgroundImage = undefined

    const svgString = canvas.toSVG({
        suppressPreamble: true,
    })

    if (!fonts || fonts.length === 0) {
        return svgString
    }

    return addFontsToSVG(svgString, fonts, customization)
}

const addFontsToSVG = (svgString: string, fonts: Font[], customization: Customization) => {
    // parse to add fonts to defs
    const parser = new DOMParser()
    const doc = parser.parseFromString(svgString, 'image/svg+xml')
    const svg = doc.documentElement

    const SVG_NS = 'http://www.w3.org/2000/svg'

    // add font faces to the SVG
    const fontsToAdd = getFonts(fonts, customization)

    if (fontsToAdd.length > 0) {
        const defs = document.createElementNS(SVG_NS, 'defs')
        const style = document.createElementNS(SVG_NS, 'style')
        style.textContent = fontsToAdd
            .map((font) => {
                return `@font-face { font-family: '${font.name}'; src: url('${font.base64}'); }`
            })
            .join('\n')

        defs.appendChild(style)

        svg.insertBefore(defs, svg.firstChild)
    }

    // after adding the fonts, we need to serialize the SVG again
    const serializer = new XMLSerializer()
    return serializer.serializeToString(svg)
}

export const exportTemplateSvg = (
    template: StoredTemplate,
    customization: Customization,
    fonts: Font[]
) => {
    const converted = convertToTemplateString(template, fonts, customization)

    if (!converted) {
        return
    }

    const blob = new Blob([converted], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

    link.href = url
    link.download = `${customization.id}-${template.id}-${timestamp}.svg`
    link.click()
    URL.revokeObjectURL(url)
}
export const exportPdfPerPage = async (
    templates: StoredTemplate[],
    customization: Customization,
    fonts: Font[] = []
) => {
    const doc = new jsPDF({
        unit: 'mm',
    })
    doc.deletePage(1) // remove the first empty page
    const parser = new DOMParser()

    getFonts(fonts, customization)
        .forEach((font) => {
            if (font.base64) {
                const file = getFontFile(font.name, font.base64)

                doc.addFileToVFS(file.filename, file.base64)
                doc.addFont(
                    `${file.filename}`,
                    font.name,
                    'normal'
                )
                doc.setFont(font.name, 'normal')
            }
        })

    for (const template of templates) {
        const svgString = convertToTemplateString(template, [], customization)
        if (!svgString) continue

        const factor = template.measurement
            ? template.measurement.real_length / template.measurement.length
            : 1
        const size = Math.round(1000 * factor)

        const svgElement = parser.parseFromString(
            svgString,
            'image/svg+xml'
        ).documentElement
        doc.addPage([size, size], 'landscape')

        //
        await svg2pdf(svgElement, doc, {
            x: 0,
            y: 0,
            width: size,
            height: size,
        })
    }

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

    return doc.save(`${customization.id}-customization-${timestamp}.pdf`)
}

export const convertToSpotString = async (
    customization: Customization,
    template: StoredTemplate,
    spotId: number,
    fonts?: Font[]
) => {
    const spot = template.spots.find((s) => s.id === spotId)

    if (!spot) {
        return
    }

    const clipPath = loadClipPath(spot)
    const bounds = clipPath.getBoundingRect()

    const canvas = new fabric.Canvas(null, {
        height: bounds.height,
        width: bounds.width,
        renderOnAddRemove: true,
        backgroundColor: 'white',
    })

    const addedInputs = await loadInputs(
        customization,
        template,
        canvas,
        spotId
    )

    if (addedInputs === 0) {
        return
    }

    canvas.setViewportTransform([1, 0, 0, 1, -bounds.left, -bounds.top])

    canvas._objects = canvas._objects.map((obj) => {
        if (obj.type !== 'image') return rescaleFabricObject(obj)
        return obj
    })

    // remove background image
    canvas.backgroundImage = undefined

    const svgString = canvas.toSVG({
        suppressPreamble: true,
    })

    if (!fonts || fonts.length === 0) {
        return svgString
    }

    return addFontsToSVG(svgString, fonts, customization)
}

export const exportPdfPerSpot = async (
    customization: Customization,
    templates: StoredTemplate[],
    fonts: Font[] = []
) => {
    const doc = new jsPDF({
        unit: 'mm',
    })
    doc.deletePage(1)
    const parser = new DOMParser()

    // Schriftarten hinzufügen
    getFonts(fonts, customization)
        .forEach((font) => {
            if (font.base64) {
                const file = getFontFile(font.name, font.base64)

                doc.addFileToVFS(file.filename, file.base64)
                doc.addFont(
                    file.filename,
                    font.name,
                    'normal'
                )
                doc.setFont(font.name, 'normal')
            }
        })

    // Für jeden Spot eine neue Seite erstellen
    for (const template of templates) {
        for (const spot of template.spots) {
            const svgString = await convertToSpotString(
                customization,
                template,
                spot.id,
                []
            )
            if (!svgString) continue

            const clipPath = loadClipPath(spot)
            const bounds = clipPath.getBoundingRect()

            // Umrechnungsfaktor von Pixel zu Millimeter
            const factor = template.measurement
                ? template.measurement.real_length / template.measurement.length
                : 1
            const width = Math.round(bounds.width * factor)
            const height = Math.round(bounds.height * factor)
            const orientation = width > height ? 'landscape' : 'portrait'

            const svgElement = parser.parseFromString(
                svgString,
                'image/svg+xml'
            ).documentElement
            doc.addPage([width, height], orientation)

            await svg2pdf(svgElement, doc, {
                x: 0,
                y: 0,
                width: width,
                height: height,
            })
        }
    }

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
    return doc.save(`${customization.id}-spots-${timestamp}.pdf`)
}

export const getFonts = (fonts: Font[], customization: Customization) => {
    const customizationFonts = [...new Set(
        customization.inputs.filter(
            (input) => input.type === 'text').map((i) => i.font_family)
    )
    ]
    return fonts.filter(
        (font) => font.base64?.length && font.base64.startsWith('data:font') && customizationFonts.includes(font.name)
    )
}

export const getFontFile = (fontName: string, base64: string) => {
    const [mimeHeader, base64Data] = base64.split(',')
    let fileExtension = mimeHeader.split('/')[1].split(';')[0]
    if (fileExtension === 'sfnt') fileExtension = 'ttf'

    return {
        'filename': `${fontName}.${fileExtension}`,
        'base64': base64Data
    }
}